// Per-service detailed content for /services/:slug pages
// Each service gets a structured layout with SEO-optimized copy.

const COMMON_BENEFITS = [
  "Saves you time and physical strain",
  "Reduces moving-day stress and uncertainty",
  "Protects your floors, furniture, and valuables",
  "Efficient, organized crews — no wasted hours",
  "Transparent, upfront pricing — no surprises",
];

const COMMON_WHY_US = [
  "Licensed & insured Wyoming moving company",
  "Experienced, background-checked movers",
  "Affordable, transparent pricing — no hidden fees",
  "Local expertise across Wyoming since 1980",
  "Open 24 hours · 4.5★ from 60+ customer reviews",
  "Excellent customer service from quote to unpack",
];

const COMMON_PROCESS = [
  {
    title: "Request a Free Quote",
    text: "Use the Instant Quote wizard, fill the form, or call (307) 265-8291. We respond fast with a clear, upfront estimate.",
  },
  {
    title: "Schedule Your Move",
    text: "We confirm your date, build a custom plan, and lock in your crew, truck, and any packing or specialty needs.",
  },
  {
    title: "We Handle Everything",
    text: "Our trained Wyoming movers arrive on time, protect your home, and move every item carefully — start to finish.",
  },
];

const COMMON_PRICING = {
  title: "Transparent Pricing — No Hidden Fees",
  text:
    "Every Crosstown Movers quote is built specifically for your move and your home. We base pricing on crew size, hours, distance, and any specialty items — and we tell you the full cost up front. No mystery surcharges, no fuel add-ons at the end. Free, no-obligation estimates by phone, online, or in-home.",
  bullets: [
    "Free quotes — by phone, online, or in-home",
    "Hourly rates for local moves; flat rates for long-distance",
    "All standard equipment, pads, and floor protection included",
    "No fuel surcharges, no fake fees",
  ],
};

const COMMON_AREAS = [
  "Casper, WY",
  "Mills, WY",
  "Cheyenne, WY",
  "Laramie, WY",
  "Douglas, WY",
  "Riverton, WY",
  "Lander, WY",
  "Evansville, WY",
  "Bar Nunn, WY",
  "Glenrock, WY",
];

export const SERVICE_CONTENT = {
  "residential-moving-casper-wy": {
    h1: "Residential Moving in Casper, WY",
    seoTitle: "Residential Movers Casper WY | Trusted Home Moving | Crosstown Movers",
    metaDesc:
      "Professional residential movers in Casper, Cheyenne & Wyoming. Licensed & insured home moving since 1980. Free quotes. Call (307) 265-8291.",
    subheadline:
      "Fast, careful, and stress-free home moves across Casper, Cheyenne, Laramie, and all of Wyoming.",
    intro: [
      "Crosstown Movers is the trusted name in residential moving in Casper, WY and across Wyoming. For more than 45 years, we've helped families pack up, move out, and settle into new homes — apartments, condos, and houses of every size — without the chaos most people associate with moving day.",
      "Our experienced residential movers serve Casper, Mills, Cheyenne, Laramie, and the surrounding Wyoming areas. From a one-bedroom apartment across town to a five-bedroom home across the state, we bring the same care, equipment, and friendly crews to every move.",
      "We're a fully licensed and insured Wyoming moving company. Free quotes, transparent pricing, and a 4.5-star track record of taking great care of homes — and the people in them.",
    ],
    includes: [
      "Free in-home or virtual moving estimates",
      "Trained, background-checked Casper movers",
      "Full floor and doorway protection on every job",
      "Furniture disassembly and reassembly",
      "Careful handling of appliances and heavy furniture",
      "Stairs, elevator, and tight-access experience",
      "Optional packing and unpacking services",
      "White-glove service for antiques and fragile items",
      "Optional short-term and long-term storage",
    ],
    benefits: COMMON_BENEFITS,
    whyUs: COMMON_WHY_US,
    process: COMMON_PROCESS,
    pricing: COMMON_PRICING,
    areas: COMMON_AREAS,
    faqs: [
      {
        q: "How much does residential moving cost in Casper, WY?",
        a: "Local home moves in Casper typically range from $400 to $1,500 depending on the size of your home, crew size, and hours required. Long-distance and out-of-town residential moves are quoted by weight and distance. Every Crosstown Movers quote is upfront with no hidden fees.",
      },
      {
        q: "How far in advance should I book residential movers?",
        a: "We recommend booking 2–4 weeks in advance for the best availability in Casper, Cheyenne, and surrounding Wyoming areas. Peak season (May–September) fills up fastest. We also accept last-minute and same-week residential moves whenever our schedule allows.",
      },
      {
        q: "Do you offer packing services for residential moves?",
        a: "Yes — full-service packing, partial packing (kitchen or fragile items only), and unpacking services are all available. We bring high-quality boxes, paper, bubble wrap, and custom materials.",
      },
      {
        q: "Are your residential movers insured?",
        a: "Absolutely. Crosstown Movers is fully licensed and insured. Every residential move includes liability and cargo coverage, and proof of insurance is included with each quote.",
      },
      {
        q: "What items can residential movers not move?",
        a: "We can't legally transport hazardous materials (gasoline, propane, paint, fireworks, chemicals), perishable food, plants on long-distance moves, or valuables like cash and important documents. We're happy to help you plan around those exceptions.",
      },
    ],
    related: ["packing-unpacking-services-wyoming", "white-glove-moving-wyoming", "furniture-moving-heavy-lifting-casper"],
  },

  "commercial-movers-casper-wy": {
    h1: "Commercial Movers in Casper, WY",
    seoTitle: "Commercial Movers Casper WY | Office Moving Wyoming | Crosstown Movers",
    metaDesc:
      "Professional commercial movers in Casper & Wyoming. Office relocation, after-hours moves, minimal downtime. Licensed & insured. Call (307) 265-8291.",
    subheadline:
      "Efficient, organized commercial moving for Wyoming businesses — minimal downtime, on time, every time.",
    intro: [
      "Crosstown Movers is Wyoming's experienced commercial moving company, serving Casper, Mills, Cheyenne, Laramie, Douglas, Riverton, Lander, and the surrounding areas. We've handled office relocations for clinics, retail, schools, law firms, and corporate offices across the state since 1980.",
      "Business downtime is expensive — that's why we plan every commercial move down to the hour. From workstation disassembly and IT relocation to after-hours weekend moves, our trained commercial movers in Casper get your team back to work fast.",
      "Fully licensed and insured, with the equipment, crew size, and project management to handle businesses of every size. Free quotes for any Wyoming commercial move.",
    ],
    includes: [
      "Office furniture disassembly, transport, and reassembly",
      "Cubicle, desk, and modular system relocation",
      "IT equipment and server relocation",
      "Conference room and executive furniture handling",
      "Retail store fixtures and inventory moves",
      "Secure file and document handling",
      "Floor plan coordination and crew labeling",
      "After-hours, weekend, and phased moves",
      "Optional commercial packing and unpacking",
      "Optional short-term storage during transitions",
    ],
    benefits: [
      "Minimal business downtime — phased and after-hours options",
      "Organized, project-managed move with clear timeline",
      "Trained crews who handle IT and modular systems",
      "Inventory and labeling — nothing gets lost",
      "Transparent pricing with no surprise charges",
    ],
    whyUs: COMMON_WHY_US,
    process: COMMON_PROCESS,
    pricing: COMMON_PRICING,
    areas: COMMON_AREAS,
    faqs: [
      {
        q: "How much does commercial moving cost in Casper, WY?",
        a: "Commercial move pricing varies by office size, distance, equipment, and timing (after-hours weekends cost more than weekday daytime moves). Crosstown Movers builds a custom proposal for every commercial move with transparent line items — no hidden fees.",
      },
      {
        q: "How far in advance should I book commercial movers?",
        a: "We recommend booking 4–6 weeks in advance for medium-to-large office moves so we can plan IT, phased moves, and any after-hours coordination. Smaller offices and last-minute commercial moves are doable with as little as a few days' notice.",
      },
      {
        q: "Do you offer packing for commercial moves?",
        a: "Yes — we offer full or partial commercial packing services, secure document handling, and IT-grade packing for monitors, servers, and equipment. We can also provide labeled bins and boxes ahead of moving day.",
      },
      {
        q: "Are your commercial movers insured?",
        a: "Yes — Crosstown Movers is fully licensed and insured for commercial moves. We carry full liability and cargo coverage and provide a Certificate of Insurance to your building or facility manager on request.",
      },
      {
        q: "What items can commercial movers not move?",
        a: "We can't legally move hazardous chemicals, flammable materials, or biohazards. For high-value or high-security items (large servers, vaults, lab equipment) we discuss custom handling during the quote.",
      },
    ],
    related: ["office-movers-casper-wy", "furniture-moving-heavy-lifting-casper", "packing-unpacking-services-wyoming"],
  },

  "local-movers-mills-wy": {
    h1: "Local Movers in Mills, WY",
    seoTitle: "Local Movers Mills WY | Same-Day Moving | Crosstown Movers",
    metaDesc:
      "Local movers in Mills, Casper & surrounding Wyoming areas. Same-week & last-minute moves. Open 24 hours. Free quotes — call (307) 265-8291.",
    subheadline:
      "Fast, friendly, and affordable local moving services in Mills, Casper, and the surrounding Wyoming areas.",
    intro: [
      "Crosstown Movers is your trusted local moving company in Mills, WY. Headquartered right at 4941 Highway St, Mills, WY 82604, we've been the go-to local movers for Mills, Casper, Evansville, Bar Nunn, and central Wyoming since 1980.",
      "Being local means faster response times, flexible scheduling, and deep knowledge of every street and neighborhood in the area. Our local movers in Casper and Mills are minutes away from most homes — and we offer hourly local rates, single-item moves, and same-week bookings.",
      "Open 24 hours, fully licensed and insured, and rated 4.5 stars by our Wyoming neighbors. Whether it's a quick local apartment move or a single piece of heavy furniture, we handle it with care.",
    ],
    includes: [
      "Hourly local moving rates",
      "Same-week and last-minute bookings",
      "Single-item moves (piano, safe, appliance, gun safe)",
      "Apartment, condo, and house moves",
      "Loading & unloading only (truck, container, PODS)",
      "Furniture disassembly and reassembly",
      "Floor and doorway protection",
      "Friendly, polite, professional crews",
      "Evening, weekend, and overnight availability",
    ],
    benefits: COMMON_BENEFITS,
    whyUs: COMMON_WHY_US,
    process: COMMON_PROCESS,
    pricing: {
      ...COMMON_PRICING,
      text:
        "Local moves in Mills and Casper are billed hourly with a clear minimum. Crosstown Movers gives upfront pricing — no fuel surcharges, no hidden fees, and no overtime tricks. Free, no-obligation quotes by phone or online.",
    },
    areas: COMMON_AREAS,
    faqs: [
      {
        q: "How much do local movers cost in Mills, WY?",
        a: "Local moves in Mills are typically billed hourly with crew size and truck included. Most local moves fall between $300 and $1,200 depending on home size and hours. Call (307) 265-8291 for a free quote.",
      },
      {
        q: "Do you do same-day or last-minute local moves?",
        a: "Yes — we're open 24 hours and regularly book same-week and last-minute local moves in Mills and Casper. Availability is best on weekday mornings; call us as early as possible.",
      },
      {
        q: "Do you offer packing services for local moves?",
        a: "Yes — full or partial packing is available, plus specialty packing for fragile and high-value items. You can also buy boxes and supplies directly from us.",
      },
      {
        q: "Are your local movers insured?",
        a: "Absolutely — Crosstown Movers is fully licensed and insured. Every local move includes full liability and cargo coverage.",
      },
      {
        q: "What items will you not move locally?",
        a: "We can't transport hazardous materials, flammable liquids, or perishable items. We're happy to advise on the few exceptions during your quote.",
      },
    ],
    related: ["residential-moving-casper-wy", "furniture-moving-heavy-lifting-casper", "packing-unpacking-services-wyoming"],
  },

  "white-glove-moving-wyoming": {
    h1: "White-Glove Moving in Wyoming",
    seoTitle: "White Glove Moving Wyoming | Premium Movers | Crosstown Movers",
    metaDesc:
      "Premium white-glove moving in Wyoming. Antiques, art, pianos, luxury furniture handled with museum-level care. Call (307) 265-8291.",
    subheadline:
      "Museum-level care for high-value items, luxury homes, and irreplaceable belongings — anywhere in Wyoming.",
    intro: [
      "Crosstown Movers' white-glove moving service is the premium standard for Wyoming homes that deserve more than a standard move. We've spent more than 45 years building Wyoming's most experienced white-glove crews — trained for fine art, antiques, pianos, electronics, and luxury furniture across Casper, Cheyenne, Laramie, and the surrounding areas.",
      "Every piece is inventoried, custom-wrapped, padded, and transported with specialty equipment. Floor and wall protection, careful in-home placement, and attentive unpacking are part of every white-glove move.",
      "Fully licensed, fully insured, and discreet. The right Wyoming moving company for high-value homes, estates, and design staging.",
    ],
    includes: [
      "Custom crating and padded wrapping",
      "Art, antique, and heirloom handling",
      "Piano and gun safe moving",
      "High-end electronics and chandeliers",
      "Inventory and condition reporting",
      "In-home placement and setup",
      "Careful unpacking and debris removal",
      "Coordination with designers and stagers",
      "Optional climate-controlled storage",
    ],
    benefits: [
      "Peace of mind for irreplaceable items",
      "Museum-level wrapping and padding",
      "Discreet, white-glove crews",
      "Inventory, condition reports, full transparency",
      "End-to-end care: pack → transport → place → unpack",
    ],
    whyUs: COMMON_WHY_US,
    process: COMMON_PROCESS,
    pricing: {
      ...COMMON_PRICING,
      title: "White-Glove Pricing — Worth Every Penny",
      text:
        "White-glove moves are quoted custom based on the items, scope, and destination. We provide a clear, written quote and never inflate prices because of the value of your belongings. Free, no-obligation quotes — phone, online, or in-home.",
    },
    areas: COMMON_AREAS,
    faqs: [
      {
        q: "How much does white-glove moving cost in Wyoming?",
        a: "White-glove pricing depends on the number and type of items, custom packaging needs, distance, and any specialty equipment. Quotes are itemized and transparent — call (307) 265-8291 for a custom estimate.",
      },
      {
        q: "How far in advance should I book white-glove movers?",
        a: "We recommend 3–6 weeks for white-glove moves so we can pre-build crates, schedule specialty equipment, and assign our most experienced crews.",
      },
      {
        q: "Do you offer specialty packing for fragile items?",
        a: "Yes — every white-glove move includes specialty wrapping. We custom-build crates for fine art, chandeliers, sculptures, and other irreplaceable pieces.",
      },
      {
        q: "Is white-glove moving insured?",
        a: "Yes. Crosstown Movers carries full liability and cargo coverage on every move. Additional valuation coverage is available for high-value items on request.",
      },
      {
        q: "What items qualify for white-glove service?",
        a: "Fine art, antiques, pianos, gun safes, luxury furniture, designer pieces, electronics, chandeliers, and any item of high monetary or sentimental value.",
      },
    ],
    related: ["residential-moving-casper-wy", "furniture-moving-heavy-lifting-casper", "packing-unpacking-services-wyoming"],
  },

  "furniture-moving-heavy-lifting-casper": {
    h1: "Furniture Moving & Heavy Lifting in Casper, WY",
    seoTitle: "Furniture Movers & Heavy Lifting Casper WY | Crosstown Movers",
    metaDesc:
      "Heavy furniture moving in Casper & Wyoming. Pianos, safes, appliances, gym equipment. Same-week service. Call (307) 265-8291.",
    subheadline:
      "Single-item or full-room — Wyoming's go-to crew for heavy furniture, pianos, safes, and appliances.",
    intro: [
      "Need large furniture or heavy appliances moved across Casper or anywhere in Wyoming? Crosstown Movers brings the muscle, the equipment, and the experience. Whether it's a single sectional or a garage full of gym equipment, our heavy lifting crews in Casper get it done safely.",
      "We specialize in pianos, gun safes, refrigerators, treadmills, sectionals, and oversized awkward items — across Casper, Mills, Cheyenne, Laramie, Douglas, Riverton, Lander, and surrounding Wyoming areas.",
      "Fully licensed, fully insured, and equipped with the right dollies, ramps, straps, pads, and stair climbers for every job.",
    ],
    includes: [
      "Pianos (upright and baby grand)",
      "Gun safes and heavy safes",
      "Refrigerators, washers, dryers",
      "Treadmills, ellipticals, home gyms",
      "Sectionals, armoires, wardrobes",
      "Pool tables and hot tubs (on request)",
      "Office furniture and heavy desks",
      "Single items or full homes",
      "Stairs, tight hallways, and elevators",
    ],
    benefits: COMMON_BENEFITS,
    whyUs: COMMON_WHY_US,
    process: COMMON_PROCESS,
    pricing: COMMON_PRICING,
    areas: COMMON_AREAS,
    faqs: [
      {
        q: "How much does heavy furniture moving cost in Casper?",
        a: "Single-item heavy moves in Casper typically range from $150 to $600 depending on the item, stairs, and distance. Pianos and safes are quoted custom. Call (307) 265-8291 for a free estimate.",
      },
      {
        q: "How far in advance should I book?",
        a: "We can often book heavy lifting jobs same week and even same day. For pianos and safes we recommend 3–7 days notice so we can assign the right crew and equipment.",
      },
      {
        q: "Do you handle stairs and tight access?",
        a: "Yes — our crews are equipped with stair climbers, dollies, ramps, and the experience to navigate tight hallways, narrow doorways, and multiple flights.",
      },
      {
        q: "Are heavy furniture movers insured?",
        a: "Yes. Every move from Crosstown Movers is fully licensed and insured with full liability and cargo coverage.",
      },
      {
        q: "What items will you not move?",
        a: "Hazardous chemicals, propane tanks, firearms (we move empty safes), flammable items, and live animals. Some pool tables require coordination with a separate technician for re-leveling.",
      },
    ],
    related: ["residential-moving-casper-wy", "white-glove-moving-wyoming", "local-movers-mills-wy"],
  },

  "packing-unpacking-services-wyoming": {
    h1: "Packing & Unpacking Services in Wyoming",
    seoTitle: "Packing & Unpacking Services Wyoming | Crosstown Movers",
    metaDesc:
      "Professional packing & unpacking in Casper, Cheyenne & all of Wyoming. Full-service or partial packing. Free quotes. Call (307) 265-8291.",
    subheadline:
      "Professional packing for homes and offices — kitchens to keepsakes, packed safely and efficiently.",
    intro: [
      "Professional packing is the difference between a stressful move and a smooth one. Crosstown Movers' packing and unpacking services in Wyoming use the right materials and a proven system to protect everything — glassware to electronics — across Casper, Cheyenne, Laramie, Mills, and surrounding Wyoming areas.",
      "From full-home packing to specialty crating for fragile and high-value items, our trained packers arrive with quality boxes, paper, bubble wrap, and custom materials. We label every box and inventory every room.",
      "Licensed, insured, and rated 4.5 stars across 60+ Wyoming reviews. Free quotes, no hidden fees, and packers who treat your belongings with real respect.",
    ],
    includes: [
      "Full-home packing (kitchen, closets, garage, attic, basement)",
      "Partial packing (kitchen-only or fragile items only)",
      "Specialty packing for art, antiques, and electronics",
      "Custom crating for high-value items",
      "Professional labeling and room-by-room inventory",
      "Quality boxes, paper, bubble wrap, and tape",
      "Wardrobe boxes and dish-pack systems",
      "Unpacking and box removal after your move",
      "Box and supply delivery before moving day",
    ],
    benefits: [
      "Saves you weeks of packing time",
      "Professional materials prevent damage",
      "Organized labeling speeds up unpacking",
      "Lowers risk of broken items vs DIY",
      "Frees you up to focus on the move logistics",
    ],
    whyUs: COMMON_WHY_US,
    process: COMMON_PROCESS,
    pricing: COMMON_PRICING,
    areas: COMMON_AREAS,
    faqs: [
      {
        q: "How much does professional packing cost in Wyoming?",
        a: "Packing is typically billed by the hour plus materials. A standard 3-bedroom home runs roughly $400–$1,000 in packing labor and supplies. We provide a clear, itemized quote — no hidden fees.",
      },
      {
        q: "How far in advance should I book packing?",
        a: "1–2 weeks ahead is ideal. For full packing of large homes, book 3–4 weeks out so we can schedule the right crew and order any specialty materials.",
      },
      {
        q: "Do you offer unpacking too?",
        a: "Yes — we unpack, place items where you want them, and haul away the empty boxes and packing material. Most clients find this is the most overlooked, highest-value part of the move.",
      },
      {
        q: "Is packing insured?",
        a: "Yes — Crosstown Movers carries full liability and cargo coverage on every job, including the packing portion.",
      },
      {
        q: "What items can't you pack?",
        a: "Hazardous materials, flammables, perishables, propane tanks, fireworks, and chemicals. Cash, jewelry, and important documents we recommend you transport personally.",
      },
    ],
    related: ["residential-moving-casper-wy", "white-glove-moving-wyoming", "office-movers-casper-wy"],
  },

  "office-movers-casper-wy": {
    h1: "Office Movers in Casper, WY",
    seoTitle: "Office Movers Casper WY | After-Hours Office Moving | Crosstown Movers",
    metaDesc:
      "Casper's trusted office movers. After-hours weekend office relocation across Wyoming. Licensed & insured. Call (307) 265-8291.",
    subheadline:
      "Casper's go-to office movers for fast, organized commercial relocation across Wyoming.",
    intro: [
      "Crosstown Movers is Casper's trusted office moving company. We handle office relocations for Wyoming businesses of every size — from small startups and clinics to multi-floor corporate offices and retail chains across Casper, Mills, Cheyenne, Laramie, Douglas, Riverton, and Lander.",
      "Our office movers coordinate every detail: floor plans, labeling, disassembly, IT, transport, and reinstallation. After-hours and weekend office moves are our specialty — your team walks in Monday morning to a ready-to-work office.",
      "Fully licensed, fully insured, and rated 4.5 stars by Wyoming clients. Free quotes for any office move, large or small.",
    ],
    includes: [
      "Workstation and cubicle disassembly and reassembly",
      "Executive office and conference room handling",
      "IT equipment and server relocation",
      "Secure file and document handling",
      "Modular office systems (Herman Miller, Steelcase, etc.)",
      "Floor plan coordination and crew labeling",
      "After-hours, weekend, and phased moves",
      "Optional packing and unpacking",
      "Optional short-term storage during transitions",
    ],
    benefits: [
      "Minimal business downtime",
      "After-hours and weekend availability",
      "IT-savvy crews — your tech moves safely",
      "Project-managed move with a clear timeline",
      "Transparent, line-item pricing",
    ],
    whyUs: COMMON_WHY_US,
    process: COMMON_PROCESS,
    pricing: COMMON_PRICING,
    areas: COMMON_AREAS,
    faqs: [
      {
        q: "How much does office moving cost in Casper, WY?",
        a: "Office moves are quoted custom based on size, distance, and timing. After-hours and weekend moves carry a small premium. Crosstown Movers builds a clear, itemized proposal — no surprise charges.",
      },
      {
        q: "How far in advance should I book an office move?",
        a: "4–6 weeks ahead is ideal for medium-to-large offices so we can plan IT, phasing, and any after-hours coordination. Smaller offices can often book within 1–2 weeks.",
      },
      {
        q: "Do you offer office packing services?",
        a: "Yes — full or partial commercial packing, secure document handling, and IT-grade packing. We can deliver boxes and bins to your office before moving day.",
      },
      {
        q: "Are your office movers insured?",
        a: "Yes — Crosstown Movers is fully licensed and insured with full liability and cargo coverage. Certificates of Insurance are provided to building or facility managers on request.",
      },
      {
        q: "What items can office movers not move?",
        a: "Hazardous chemicals, biohazards, and flammable materials. For sensitive, high-security, or oversized items (large servers, vaults, lab equipment) we coordinate special handling during the quote.",
      },
    ],
    related: ["commercial-movers-casper-wy", "furniture-moving-heavy-lifting-casper", "packing-unpacking-services-wyoming"],
  },
};
